import React, { useState, useEffect } from "react";
import axios from "axios";
import { API_URL } from "../../utils/Url";
import { Header } from "../header/Header";
import { Sidebar } from "../sidebar/Sidebar";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

export const MembershipPlan = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const getPlans = async () => {
    try {
      setLoading(true);
      const token = sessionStorage.getItem("token");
      const { data } = await axios.get(`${API_URL}/api/admin/get/plans`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setPlans(data || []);
    } catch (error) {
      console.error("Fetch Plans Error:", error);
      toast.error("Failed to sync membership plan metrics");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getPlans();
  }, []);

  const togglePlanStatus = async (id, currentStatus) => {
    const token = sessionStorage.getItem("token");
    if (!token) {
      toast.error("Session verification token missing");
      return;
    }

    // Optimistic UI Update for zero latency feel
    const originalPlans = plans;
    setPlans((prev) =>
      prev.map((plan) =>
        plan._id === id ? { ...plan, isActive: !currentStatus } : plan,
      ),
    );

    try {
      await axios.patch(
        `${API_URL}/api/admin/status/plan/${id}`,
        { isActive: !currentStatus },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      toast.success("Plan accessibility rule updated");
    } catch (error) {
      // Revert if endpoint drops integration execution
      setPlans(originalPlans);
      console.error("Status Toggle Error:", error);
      toast.error("Failed to alter plan status configuration");
    }
  };

  const deletePlan = async (id) => {
    const confirmDelete = window.confirm(
      "Are you absolutely sure you want to permanently delete this subscription model?",
    );

    if (!confirmDelete) return;

    const originalPlans = plans;
    setPlans((prev) => prev.filter((plan) => plan._id !== id));

    try {
      const token = sessionStorage.getItem("token");
      await axios.delete(`${API_URL}/api/admin/delete/plan/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      toast.success("Membership matrix record eliminated 🗑️");
    } catch (error) {
      setPlans(originalPlans);
      console.error("Delete Plan Error:", error);
      toast.error("Error breaking down target subscription data layout");
    }
  };

  return (
    <>
      <Header />
      <Sidebar />

      <div
        className="main-content mt-5 px-3 px-md-4 py-4"
        style={{ backgroundColor: "#f8fafc", minHeight: "100vh" }}
      >
        {/* PREMIUM INSIGHT CONTROL PANEL HEADER */}
        <div className="d-flex flex-column flex-sm-row justify-content-between align-items-sm-center gap-3 mb-4 p-3 bg-white border rounded-4 shadow-sm">
          <div>
            <h2 className="fw-bold text-gray-800 mb-0 fs-3">
              Subscription Engines
            </h2>
            <p className="text-muted small mb-0">
              Configure tiers, structural prices, duration rules, and user
              monetization levels
            </p>
          </div>
          <button
            className="btn btn-primary rounded-3 fw-semibold px-3 py-2 d-inline-flex align-items-center gap-1 shadow-sm"
            onClick={() => navigate("/create/plans")}
          >
            <i className="bi bi-patch-plus fs-5 line-height-0"></i> Add New Tier
          </button>
        </div>

        {/* CORE DATA CARD CONTAINER */}
        <div className="card border-0 rounded-4 shadow-sm overflow-hidden">
          <div className="card-body p-0">
            {loading ? (
              <div className="d-flex flex-column align-items-center justify-content-center py-5">
                <div
                  className="spinner-border text-primary mb-3"
                  role="status"
                ></div>
                <span className="text-muted small fw-medium">
                  Parsing monetization database schema...
                </span>
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-hover align-middle mb-0 custom-admin-table">
                  <thead className="bg-light border-bottom">
                    <tr>
                      <th
                        className="text-muted text-uppercase fs-7 fw-bold px-4 py-3"
                        style={{ width: "60px" }}
                      >
                        #
                      </th>
                      <th className="text-muted text-uppercase fs-7 fw-bold py-3">
                        Tier Blueprint
                      </th>
                      <th className="text-muted text-uppercase fs-7 fw-bold py-3">
                        Price Rate
                      </th>
                      <th className="text-muted text-uppercase fs-7 fw-bold py-3">
                        Validity Span
                      </th>
                      <th
                        className="text-muted text-uppercase fs-7 fw-bold py-3"
                        style={{ maxWidth: "350px" }}
                      >
                        Bundled Features
                      </th>
                      <th className="text-muted text-uppercase fs-7 fw-bold px-4 py-3 text-end">
                        Operational Controls
                      </th>
                    </tr>
                  </thead>

                  <tbody className="border-0">
                    {plans?.length > 0 ? (
                      plans.map((plan, index) => (
                        <tr
                          key={plan._id}
                          className="align-middle transition-all"
                        >
                          {/* Counter index */}
                          <td className="px-4 fw-semibold text-muted">
                            {index + 1}
                          </td>

                          {/* Plan Name */}
                          <td>
                            <div className="d-flex align-items-center gap-2">
                              <span
                                className={`badge rounded-circle p-1 d-inline-block ${plan.isActive ? "bg-success" : "bg-secondary"}`}
                              ></span>
                              <span className="fw-bold text-dark fs-6">
                                {plan.name}
                              </span>
                            </div>
                          </td>

                          {/* Plan Cost */}
                          <td>
                            <span className="font-monospace fw-bold text-dark fs-6">
                              ₹{plan.price?.toLocaleString("en-IN")}
                            </span>
                          </td>

                          {/* Duration Window */}
                          <td>
                            <span className="badge bg-light text-dark border px-2.5 py-1.5 fw-semibold text-capitalize">
                              <i className="bi bi-calendar3 me-1 text-muted"></i>{" "}
                              {plan.duration}
                            </span>
                          </td>

                          {/* Dynamic Features Segment */}
                          <td style={{ maxWidth: "350px" }}>
                            <div className="d-flex flex-wrap gap-1">
                              {plan.features && plan.features.length > 0 ? (
                                plan.features.map((feat, fIdx) => (
                                  <span
                                    key={fIdx}
                                    className="badge bg-primary-subtle text-primary border border-primary-subtle fw-medium px-2 py-1 fs-7"
                                  >
                                    {feat}
                                  </span>
                                ))
                              ) : (
                                <span className="text-muted small italic">
                                  Standard basic baseline profile configuration
                                </span>
                              )}
                            </div>
                          </td>

                          {/* Interactive Operation Engine Controls */}
                          <td className="px-4 text-end">
                            <div className="d-flex justify-content-end gap-2 flex-wrap flex-md-nowrap">
                              {/* Status Custom Toggle Trigger */}
                              <button
                                onClick={() =>
                                  togglePlanStatus(plan._id, plan.isActive)
                                }
                                className={`btn btn-sm fw-semibold px-2.5 shadow-xs border d-inline-flex align-items-center gap-1 ${
                                  plan.isActive
                                    ? "btn-success-subtle text-success border-success-subtle"
                                    : "btn-secondary-subtle text-secondary border-secondary-subtle"
                                }`}
                                title={
                                  plan.isActive
                                    ? "Click to restrict user access"
                                    : "Click to deploy live to system portfolio"
                                }
                              >
                                <i
                                  className={`bi ${plan.isActive ? "bi-eye" : "bi-eye-slash"}`}
                                ></i>
                                {plan.isActive ? "Active" : "Disabled"}
                              </button>

                              {/* Navigation Update Route Link */}
                              <button
                                onClick={() =>
                                  navigate(`/update/plan/${plan._id}`)
                                }
                                className="btn btn-sm btn-light border fw-medium text-secondary d-inline-flex align-items-center gap-1 shadow-xs px-2.5"
                              >
                                <i className="bi bi-pencil-square"></i> Edit
                              </button>

                              {/* Destruction Handler Call */}
                              <button
                                onClick={() => deletePlan(plan._id)}
                                className="btn btn-sm btn-outline-danger d-inline-flex align-items-center gap-1 px-2.5"
                              >
                                <i className="bi bi-trash3"></i> Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="6" className="text-center py-5">
                          <div className="text-muted d-flex flex-column align-items-center justify-content-center gap-2">
                            <i className="bi bi-credit-card-2-back fs-1 text-secondary"></i>
                            <p className="mb-0 fw-semibold text-dark fs-5">
                              No Membership Blueprint Configured
                            </p>
                            <p className="mb-0 text-muted small">
                              Deploy an operational premium channel to enable
                              consumer monetization pathways.
                            </p>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};
